import { z } from "zod";

import { ANALYST_LEVELS } from "./identity-events.js";

/**
 * `GET /api/v1/bank/team`, **as the OpenAPI document declares it** (`C-2`, R-1: `BANK_TEAM_MEMBER_SCHEMA` in
 * `openapi.document.ts`) , the shape the bank client PARSES at its boundary instead of casting.
 *
 * `stages` is a CLOSED map: every open stage is a key, 0 when the analyst holds none there, nothing else (a terminal stage or a
 * new key is a mismatch, not a surplus). The counts sum to `openFiles` and, weighted by the scale, to `points`: both are the
 * server's arithmetic and the screen derives none of it. `openapi.bank-team-contract.spec.ts` (in the api) holds this to the
 * document key for key; the live answer is held to it in `l5-chef-screens.e2e.spec.ts`.
 */
export const BANK_TEAM_OPEN_STAGES = [
  "nouveau",
  "revue",
  "validation",
  "offre",
  "comite",
  "signature",
  "conditions",
  "retour",
] as const;

export const bankTeamMemberSchema = z.looseObject({
  id: z.string(),
  fullName: z.string(),
  email: z.string(),
  role: z.string(),
  statut: z.enum(["invitationEnAttente", "actif", "desactive"]),
  disponibilite: z.string().nullable().optional(),
  retourLe: z.string().nullable().optional(),
  deactivatedOn: z.string().nullable().optional(),
  invitationPending: z.boolean(),
  /** `SENIORITY-LEVEL`: the person's level, set by the chef at the invite (`identity.users.seniority_level`), read by the proposal's rules (b) and (d). */
  level: z.enum(ANALYST_LEVELS),
  openFiles: z.number().int(),
  points: z.number().int(),
  stages: z.strictObject(
    Object.fromEntries(BANK_TEAM_OPEN_STAGES.map((stage) => [stage, z.number().int()])) as Record<
      (typeof BANK_TEAM_OPEN_STAGES)[number],
      z.ZodNumber
    >,
  ),
});

/**
 * `CHEF-HELD-FILES` (`D-1027-1`): the CALLER's own entry , the files the chef carries himself, computed by the same load code as the
 * analysts' rows; `null` when he holds none. The analysts' `members` never contain him.
 */
export const bankTeamSelfSchema = z.strictObject({
  id: z.string(),
  name: z.string(),
  openFiles: z.number().int(),
  points: z.number().int(),
  stages: z.strictObject(
    Object.fromEntries(BANK_TEAM_OPEN_STAGES.map((stage) => [stage, z.number().int()])) as Record<
      (typeof BANK_TEAM_OPEN_STAGES)[number],
      z.ZodNumber
    >,
  ),
});

export const bankTeamAnswerSchema = z.looseObject({
  members: z.array(bankTeamMemberSchema),
  weights: z.record(z.string(), z.number()),
  /** The door's own day, `YYYY-MM-DD` in Africa/Bamako (`EQUIPE-MODALS`): what a day typed without a year is read against. */
  asOf: z.string().regex(/^\d{4}-\d{2}-\d{2}$/u),
  /** `CHEF-HELD-FILES`: the caller's own portfolio, or `null` when he carries nothing. */
  self: bankTeamSelfSchema.nullable(),
});

export type BankTeamSelf = z.infer<typeof bankTeamSelfSchema>;
export type BankTeamMember = z.infer<typeof bankTeamMemberSchema>;
export type BankTeamAnswer = z.infer<typeof bankTeamAnswerSchema>;
